import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import JWModal from '../brand/JWModal';
import JWButton from '../brand/JWButton';
import { AddButtonIcon } from '../icons';
import axiosService from '../../utils/axios';
import { RootState } from '../../store';
import { SkillType } from '../../types/profile';
import {
    Chip,
    FooterNote,
    PRIMARY_BUTTON_CLASS,
    SectionLabel,
} from '../../pages/Onboarding/OnboardingUI';

interface AddSkillsModalProps {
    show: boolean,
    setShow: (show: boolean) => void,
    onClose: () => void,
    selectedSkills?: Array<SkillType> | undefined | null,
    setSelectedSkills: (skills: Array<SkillType>) => void,
    saveSkills : () => Promise<void>,
}

const MAX_SKILLS = 5;

const formatSkillName = (name: string | undefined) => {
    if (!name) return '';
    return name
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

function AddSkillsModal(props: AddSkillsModalProps) {
    const [skills, setSkills] = useState<SkillType[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [filterSkillText, setFilterSkillText] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    const user = useSelector((state: RootState) => state.auth.user);
    const selectedSkills = props.selectedSkills ?? [];
    const atLimit = selectedSkills.length >= MAX_SKILLS;
    const nothingPicked = selectedSkills.length === 0;

    useEffect(() => {
        if (!props.show) return undefined;
        setFilterSkillText('');
        setErrorMessage('');
        setSaving(false);

        if (!user?._id) return undefined;

        let cancelled = false;
        axiosService.get<SkillType[]>(`/skills/user?userId=${user._id}`)
            .then((skillsRes) => {
                if (cancelled) return;
                setSkills(skillsRes?.data ?? []);
                setLoadError(false);
            })
            .catch((error) => {
                console.error('Error retrieving skills: ', error);
                if (!cancelled) setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });

        return () => {
            cancelled = true;
        };
    }, [props.show, user?._id]);

    const selectedIds = useMemo(
        () => new Set(selectedSkills.map((skill) => skill._id)),
        [selectedSkills]
    );
    const query = filterSkillText.trim().toLowerCase();
    const filteredSkills = useMemo(
        () => skills.filter((skill) => {
            if (selectedIds.has(skill._id)) return false;
            if (!query) return true;
            return (skill.displayName || '').toLowerCase().includes(query);
        }),
        [skills, selectedIds, query]
    );

    const selectSkill = (skillToAdd: SkillType) => {
        if (atLimit) {
            setErrorMessage(`You can select up to ${MAX_SKILLS} skills maximum`);
            return;
        }
        props.setSelectedSkills([...selectedSkills, skillToAdd]);
        setErrorMessage('');
    };

    const removeSelectedSkill = (skillToDelete: SkillType) => {
        props.setSelectedSkills(selectedSkills.filter((skill) => skill._id !== skillToDelete._id));
        setErrorMessage('');
    };

    const createSkill = async () => {
        try {
            if (!user) return;
            const skillToCreate = filterSkillText.trim();
            if (!skillToCreate) return;
            if (atLimit) {
                setErrorMessage(`You can select up to ${MAX_SKILLS} skills maximum`);
                return;
            }
            if (skills.some((skill) => skill.displayName.toLowerCase() === skillToCreate.toLowerCase())) {
                setErrorMessage('Skill already exists');
                return;
            }
            setCreating(true);
            const res = await axiosService.post<SkillType>(
                '/skills',
                { displayName: skillToCreate },
                { params: { userId: user._id } }
            );
            const skillFromRes = res?.data;
            if (skillFromRes) {
                setSkills((current) => (
                    current.some((skill) => skill._id === skillFromRes._id)
                        ? current
                        : [...current, skillFromRes]
                ));
                props.setSelectedSkills([...selectedSkills, skillFromRes]);
                setFilterSkillText('');
            }
            setErrorMessage('');
        } catch (error) {
            console.error('Error creating skill: ', error);
            setErrorMessage('Could not add that skill. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const validateAndSaveSkills = async () => {
        if (selectedSkills.length > MAX_SKILLS) {
            setErrorMessage(`You can select up to ${MAX_SKILLS} skills maximum. Please remove some skills before saving.`);
            return;
        }
        setSaving(true);
        try {
            await props.saveSkills();
            setErrorMessage('');
        } finally {
            setSaving(false);
        }
    };

    const catalog = (
        <div className="chip-pool pool-scroll flex min-h-0 flex-1 flex-wrap content-start gap-2 overflow-y-auto overscroll-contain">
            {!loaded && skills.length === 0 && !loadError && (
                <p className="text-sm text-gray-400">Skills are still loading.</p>
            )}
            {query && loaded && filteredSkills.length === 0 && (
                <p className="text-sm text-gray-500">No skills match that search.</p>
            )}
            {!query && loaded && !loadError && skills.length === 0 && (
                <p className="text-sm text-gray-400">No skills are available right now.</p>
            )}
            {filteredSkills.map((skill) => (
                <Chip
                    key={`skill_${skill._id}`}
                    label={formatSkillName(skill.displayName || skill.name)}
                    selected={false}
                    disabled={atLimit}
                    onToggle={() => selectSkill(skill)}
                />
            ))}
        </div>
    );

    return (
        <JWModal
            title={(
                <div className="pr-8 text-center">
                    <div className="text-xl font-extrabold leading-tight text-gray-900 sm:text-[28px]">What do you do?</div>
                    <p className="mt-1 text-sm font-normal text-gray-500 sm:text-base">
                        Collaborators search by skill, so pick what you want to be found for.
                    </p>
                </div>
            )}
            show={props.show}
            onClose={props.onClose}
            setShow={props.setShow}
            wrapperClassName="flex flex-col md:w-[640px] w-full h-[92vh] max-h-[760px] overflow-hidden"
        >
            <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 flex-1 flex-col px-1 sm:px-2">
                    {loadError && (
                        <p className="mb-3 text-sm text-red">Skills didn&apos;t load. You can still add one by name.</p>
                    )}
                    {selectedSkills.length > 0 && (
                        <div className="chip-remove-row mb-4 shrink-0" role="region" aria-label="Picked">
                            <SectionLabel hint={`${selectedSkills.length}/${MAX_SKILLS}`}>Picked</SectionLabel>
                            <div className="chip-pool flex flex-wrap gap-2">
                                {selectedSkills.map((skill) => (
                                    <Chip
                                        key={`selected_${skill._id}`}
                                        label={formatSkillName(skill.displayName || skill.name)}
                                        selected
                                        removable
                                        onToggle={() => removeSelectedSkill(skill)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="shrink-0">
                        <SectionLabel hint={nothingPicked ? `0/${MAX_SKILLS} picked` : undefined}>
                            {nothingPicked ? 'Skills' : 'Select skills'}
                        </SectionLabel>
                    </div>
                    <div className="mb-3 flex shrink-0 flex-col gap-2 sm:flex-row sm:items-start">
                        <div className="flex flex-1 flex-col">
                            <input
                                value={filterSkillText}
                                id="profile-skill-search"
                                aria-label="Search for skills"
                                placeholder="Search for skills"
                                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-base focus:border-purple-light focus:outline-none focus:ring-2 focus:ring-purple-light/20"
                                onChange={(event) => {
                                    setFilterSkillText(event.target.value);
                                    setErrorMessage('');
                                }}
                            />
                            {errorMessage && (
                                <span className="mt-2 text-sm text-red">{errorMessage}</span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={createSkill}
                            disabled={atLimit || creating || !filterSkillText.trim()}
                            className={`jamboard-tags-add-btn flex h-11 cursor-pointer items-center whitespace-nowrap rounded-full bg-[#4c44c3] px-6 text-base font-bold text-white ${
                                atLimit || creating || !filterSkillText.trim()
                                    ? 'cursor-not-allowed opacity-60'
                                    : ''
                            }`}
                        >
                            <AddButtonIcon width={16} height={16} fill="white" />
                            <span className="ml-2">Add</span>
                        </button>
                    </div>
                    {catalog}
                </div>
                <div className="relative shrink-0 border-t border-gray-100 px-1 pt-3 sm:px-2">
                    {atLimit && !errorMessage && (
                        <FooterNote>Five is the max. Unselect one to swap.</FooterNote>
                    )}
                    <JWButton
                        className={`${PRIMARY_BUTTON_CLASS} jamboard-pin-continue`}
                        onClick={validateAndSaveSkills}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </JWButton>
                </div>
            </div>
        </JWModal>
    );
}

export default AddSkillsModal;
